import { SeoPageConfig } from "./seo-pages";

/**
 * Посадочные под профессиональные сценарии. Сознательно не заводим страницы
 * под медицинские приёмы и психотерапию: это специальные категории персональных
 * данных по 152-ФЗ, для них нужны отдельные правовые гарантии.
 */
export const SCENARIO_PAGES: SeoPageConfig[] = [
  {
    path: "/legal-transcription",
    cardTitle: "Для юристов",
    metaTitle: "Расшифровка аудиозаписей для юристов | Транскрибация заседаний и встреч",
    metaDescription:
      "Расшифровка аудиозаписей заседаний, консультаций и переговоров для юридической практики. Таймкоды для ссылок на конкретные моменты записи.",
    customTitle: "Расшифровка записей для юристов",
    customSubtitle: "Заседания, консультации и переговоры — в тексте, по которому можно искать и цитировать.",
    seoDescription: (
      <>
        <p>
          В юридической работе запись почти всегда нужна в виде текста: по нему ищут формулировки, готовят позицию и
          ссылаются на конкретные моменты. Ручная расшифровка часового заседания занимает четыре-пять часов, и это время
          уходит на механическую работу, а не на анализ.
        </p>
        <p>
          Таймкоды здесь важнее, чем в любом другом сценарии: они позволяют сослаться не на «где-то в середине», а на
          конкретную минуту записи, и быстро перепроверить спорную формулировку в оригинале. Разделение по спикерам
          разводит реплики сторон.
        </p>
        <p>
          Важная оговорка: это рабочий инструмент для подготовки, а не официальный протокол. Автоматическая расшифровка
          требует вычитки — особенно там, где точность формулировки имеет юридическое значение.
        </p>
        <p>
          Материал у юриста разнородный по качеству, и это определяет ожидания. Запись консультации в кабинете обычно
          чистая. Заседание, записанное с задних рядов на телефон, — совсем другая история: гулкое помещение, говорящие
          на удалении, шум зала. Второй случай стоит сразу считать черновиком и закладывать время на вычитку.
        </p>
        <p>
          Отдельная сложность — имена, названия организаций и номера. Модель хорошо восстанавливает связную речь, но
          фамилия, которую она слышит впервые, легко превращается в похожее по звучанию слово. Реквизиты, даты и ссылки
          на нормы нужно проверять по записи всегда, независимо от общего качества расшифровки.
        </p>
        <p>
          Практическая ценность текста — в поиске. По часовой записи невозможно быстро найти, где обсуждали конкретный
          пункт договора; по расшифровке это делается за секунды, а таймкод сразу показывает, куда перемотать для
          проверки формулировки.
        </p>
        <p>
          И вопрос, который стоит решить до записи, а не после: правомерность самой фиксации разговора. Правила
          различаются для судебных заседаний, переговоров и консультаций, и здесь техника ничего не решает — это область
          профессиональной ответственности.
        </p>
      </>
    ),
    faq: [
      {
        question: "Можно ли использовать расшифровку как официальный документ?",
        answer:
          "Нет. Это рабочий инструмент для подготовки и анализа. Официальные протоколы оформляются по установленной процедуре, а автоматический текст требует вычитки."
      },
      {
        question: "Насколько точно распознаётся юридическая терминология?",
        answer:
          "В целом хорошо — модель опирается на контекст предложения. Номера статей, даты и имена собственные стоит проверять вручную."
      },
      {
        question: "Как быстро найти нужный момент в записи?",
        answer:
          "По таймкодам: каждая реплика привязана ко времени, поэтому от фразы в тексте можно сразу перейти к нужной минуте записи."
      },
      {
        question: "Что с записями плохого качества?",
        answer:
          "Диктофон в кармане или запись с большого расстояния дают заметно больше ошибок. Такой текст стоит рассматривать как черновик и вычитывать полностью."
      }
    ]
  },
  {
    path: "/hr-interview-transcription",
    cardTitle: "Для HR и собеседований",
    metaTitle: "Расшифровка собеседований в текст | Транскрибация интервью для HR",
    metaDescription:
      "Расшифровка записей собеседований в текст с разделением по спикерам. Сравнивайте кандидатов по фактам, а не по памяти и обрывочным заметкам.",
    customTitle: "Расшифровка собеседований",
    customSubtitle: "Сравнение кандидатов по тексту, а не по памяти и обрывочным заметкам.",
    seoDescription: (
      <>
        <p>
          На собеседовании невозможно одновременно вести разговор и подробно записывать. В итоге решение принимается по
          обрывочным заметкам и общему впечатлению, а через неделю, когда кандидатов было десять, детали смешиваются.
          Расшифровка снимает эту проблему: разговор остаётся в тексте целиком.
        </p>
        <p>
          Разделение по спикерам разводит вопросы интервьюера и ответы кандидата, поэтому по тексту удобно сравнивать,
          как разные люди отвечали на один и тот же вопрос. AI-сводка помогает, когда нужно быстро освежить в памяти
          суть разговора перед финальным обсуждением.
        </p>
        <p>
          Практическая сторона: кандидата нужно предупредить о записи и получить согласие — это касается любой
          аудиофиксации разговора, независимо от того, как она потом обрабатывается.
        </p>
        <p>
          Главная выгода расшифровки в найме — не экономия времени, а сопоставимость. Когда ответы восьми кандидатов
          лежат текстом, их можно поставить рядом и сравнить по одному и тому же вопросу. По памяти так не получается:
          обычно выигрывает тот, кого собеседовали последним.
        </p>
        <p>
          Это же снижает влияние первого впечатления. Расшифровка фиксирует, что человек ответил, а не насколько
          уверенно он держался. Для технических и содержательных вопросов разница между этими двумя вещами бывает
          довольно большой.
        </p>
        <p>
          Практический нюанс: собеседования по видеосвязи записываются легко, а очные — как получится. Телефон на столе
          между собеседниками работает нормально в тихом кабинете и плохо в опенспейсе. Если интервью очные и
          регулярные, стоит один раз купить настольный микрофон.
        </p>
        <p>
          Хранение записей и расшифровок — отдельный вопрос. Это персональные данные кандидата, и держать их бессрочно
          «на всякий случай» не стоит: разумнее определить срок, после которого материалы по незакрытым вакансиям
          удаляются.
        </p>
      </>
    ),
    faq: [
      {
        question: "Нужно ли предупреждать кандидата о записи?",
        answer:
          "Да. Запись разговора требует согласия участников — это общее требование к аудиофиксации, а не особенность сервиса."
      },
      {
        question: "Видно ли, где говорит кандидат, а где интервьюер?",
        answer:
          "Да, реплики разделены по говорящим. Имена подставляются вручную — сервис обозначает участников как «Спикер 1» и «Спикер 2»."
      },
      {
        question: "Можно ли получить короткую выжимку по кандидату?",
        answer:
          "Да, AI-сводка соберёт краткое изложение и ключевые моменты разговора. Полный текст при этом остаётся доступен."
      },
      {
        question: "Подойдёт ли запись с телефона?",
        answer:
          "Да, если собеседник слышен разборчиво. Телефон рядом с говорящими даёт вполне рабочее качество, из дальнего угла комнаты — заметно хуже."
      }
    ]
  },
  {
    path: "/sales-calls-transcription",
    cardTitle: "Для отдела продаж",
    metaTitle: "Расшифровка звонков отдела продаж | Транскрибация переговоров",
    metaDescription:
      "Транскрибация записей звонков и переговоров для отдела продаж. Разбор возражений, обучение менеджеров и контроль договорённостей по тексту.",
    customTitle: "Расшифровка звонков и переговоров",
    customSubtitle: "Разбор возражений и контроль договорённостей по тексту, а не по памяти.",
    seoDescription: (
      <>
        <p>
          Записи звонков обычно копятся часами и почти никогда не прослушиваются: чтобы разобрать один разговор,
          руководителю нужно потратить столько же времени, сколько он длился. В текстовом виде разговор просматривается
          за пару минут, а по десяткам расшифровок можно искать повторяющиеся возражения и удачные формулировки.
        </p>
        <p>
          Отдельная польза — фиксация договорённостей. Что именно пообещали клиенту, какие сроки назвали и на каких
          условиях сошлись — всё это остаётся в тексте с привязкой ко времени, а не в пересказе менеджера.
        </p>
        <p>
          Для обучения новых сотрудников расшифровки удобнее записей: их можно раздать, прокомментировать прямо в
          документе и собрать из них подборку типичных ситуаций.
        </p>
        <p>
          Телефонная линия сжимает звук сильнее любого другого источника: срезается верх, откуда берутся согласные, и
          речь становится глуше. Точность на таких записях ниже, чем на созвонах с гарнитурой, и это стоит учитывать
          заранее. Режим «Качество» здесь оправдан почти всегда.
        </p>
        <p>
          Второй типичный источник ошибок — перебивания. В продажах собеседники говорят одновременно чаще, чем на
          совещаниях, и именно в этих местах разделение по спикерам сбивается. Такие фрагменты быстро находятся по
          таймкодам, если реплики выглядят нелогично.
        </p>
        <p>
          Самое полезное применение — не разбор одного звонка, а поиск закономерностей по десяткам. Когда разговоры
          лежат текстом, видно, на каком именно возражении разговор чаще всего заканчивается и какие формулировки
          работают у сильных менеджеров. Из записей это не вычитывается никогда — просто потому, что их никто не
          слушает.
        </p>
        <p>
          Для обучения расшифровка удобнее записи ещё и потому, что её можно обезличить. Убрать название компании и имя
          клиента из текста — минутное дело, а из аудио никак.
        </p>
      </>
    ),
    faq: [
      {
        question: "Можно ли обработать сразу много звонков?",
        answer:
          "Файлы загружаются по одному, но ограничений на количество нет. Списание идёт по суммарной длительности записей."
      },
      {
        question: "Разбираются ли короткие звонки на пару минут?",
        answer: "Да, ограничений по минимальной длине нет — расшифровываются записи любой продолжительности."
      },
      {
        question: "Как быть с телефонным качеством звука?",
        answer:
          "Телефонная линия сжимает звук, поэтому ошибок будет больше, чем на записи с гарнитуры. Для таких файлов лучше выбирать режим «Качество»."
      },
      {
        question: "Нужно ли согласие клиента на запись?",
        answer:
          "Да, о записи разговора нужно предупреждать. Это требование к аудиофиксации в целом, независимо от способа обработки."
      }
    ]
  },
  {
    path: "/ux-research-transcription",
    cardTitle: "Для UX-исследований",
    metaTitle: "Расшифровка UX-интервью в текст | Транскрибация исследований",
    metaDescription:
      "Расшифровка UX-интервью и юзабилити-тестов в текст с таймкодами. Цитаты для отчёта и материал для анализа без ручной перепечатки.",
    customTitle: "Расшифровка UX-интервью",
    customSubtitle: "Цитаты для отчёта и материал для анализа без ручной перепечатки.",
    seoDescription: (
      <>
        <p>
          В качественных исследованиях расшифровка — обязательный этап: без текста невозможно ни закодировать ответы, ни
          собрать цитаты в отчёт. При этом на одно часовое интервью уходит несколько часов перепечатки, а интервью в
          исследовании обычно восемь-двенадцать.
        </p>
        <p>
          Автоматическая расшифровка возвращает это время анализу. Текст с таймкодами позволяет вставить в отчёт точную
          цитату и при необходимости показать команде исходный фрагмент записи — а это гораздо убедительнее пересказа.
        </p>
        <p>
          Для юзабилити-тестов важны междометия и паузы, по которым видно затруднение респондента. Модель передаёт речь
          близко к тому, как она звучала, но заминки и невербальные реакции всё равно стоит помечать самому при вычитке.
        </p>
        <p>
          Юзабилити-тест отличается от интервью тем, что респондент много молчит: он смотрит на экран, ищет кнопку,
          соображает. В расшифровке эти паузы превращаются в пустоту между репликами, и без таймкодов непонятно, сколько
          человек провозился. Поэтому текст здесь работает вместе с записью экрана, а не вместо неё.
        </p>
        <p>
          Ещё одна особенность — модератор говорит мало и в основном вопросами, респондент много и сбивчиво. Разделение
          по спикерам справляется с таким распределением хорошо, и это удобно: реплики модератора служат естественными
          разделителями между заданиями.
        </p>
        <p>
          Для анализа расшифровки обычно выгружают в DOCX и размечают прямо в документе — цветом, комментариями или
          тегами. Это дешевле специализированных инструментов и достаточно, пока интервью меньше двадцати.
        </p>
        <p>
          Одна вещь, которую расшифровка не решает: она не заменяет заметки по ходу. Гипотезы, которые приходят в голову
          во время теста, в записи не окажутся — их всё равно нужно фиксировать самому.
        </p>
      </>
    ),
    faq: [
      {
        question: "Подходит ли это для дословной расшифровки?",
        answer:
          "Текст близок к оригинальной речи, но междометия и паузы передаются не всегда. Для строго дословной расшифровки текст нужно дорабатывать вручную."
      },
      {
        question: "Как собрать цитаты для отчёта?",
        answer:
          "По таймкодам: находите нужное место в тексте, копируете формулировку и при необходимости ссылаетесь на момент в записи."
      },
      {
        question: "Работает ли это с интервью по видеосвязи?",
        answer:
          "Да. Загрузите файл записи созвона — формат значения не имеет, звук из видеоконтейнера извлекается автоматически."
      },
      {
        question: "Что с записями, где респондент говорит тихо?",
        answer:
          "Тихая речь распознаётся хуже. Если запись уже сделана, выбирайте режим «Качество» — он заметно устойчивее к сложному звуку."
      }
    ]
  },
  {
    path: "/focus-group-transcription",
    cardTitle: "Для фокус-групп",
    metaTitle: "Расшифровка фокус-групп в текст | Транскрибация групповых дискуссий",
    metaDescription:
      "Расшифровка записей фокус-групп с разделением по участникам. Групповая дискуссия превращается в текст, пригодный для анализа.",
    customTitle: "Расшифровка фокус-групп",
    customSubtitle: "Групповая дискуссия — в тексте, где видно, кто что сказал.",
    seoDescription: (
      <>
        <p>
          Фокус-группа — самый тяжёлый материал для ручной расшифровки: участников шесть-десять, они перебивают друг
          друга, реагируют одновременно. Модератор физически не может фиксировать всё по ходу, а после встречи
          восстанавливать дискуссию по памяти бессмысленно.
        </p>
        <p>
          Разделение по спикерам берёт на себя самую трудоёмкую часть — определяет границы реплик и группирует их по
          говорящим. Имена подставляются вручную, зато не приходится вручную размечать двухчасовую запись.
        </p>
        <p>
          Стоит быть реалистом: моменты, где говорят одновременно, остаются сложными для любой автоматики. Такие
          фрагменты удобно находить по таймкодам и переслушивать точечно, а не переслушивать всю запись целиком.
        </p>
        <p>
          Качество записи решает здесь больше, чем в любом другом сценарии. Один микрофон в центре стола на десять
          человек даёт разную громкость у ближних и дальних участников, и модель начинает путать голоса. Два-три
          микрофона по периметру или петлички меняют результат кардинально.
        </p>
        <p>
          Полезный приём — попросить участников назвать себя в начале. Тогда при вычитке первые реплики позволяют
          сопоставить «Спикера 1» с конкретным человеком, и дальше по тексту разметка восстанавливается сама.
        </p>
        <p>
          Групповая динамика создаёт специфический материал: короткие реплики, согласия, смех, реакции на чужие слова.
          Это ценно для анализа, но плохо ложится в связный текст — расшифровка фокус-группы почти всегда выглядит
          рванее, чем расшифровка интервью, и это нормально.
        </p>
        <p>
          Для отчёта из такого текста обычно берут не цитаты целиком, а формулировки, которые повторялись у разных
          участников. Найти их поиском по расшифровке в разы быстрее, чем выуживать из двухчасовой записи.
        </p>
      </>
    ),
    faq: [
      {
        question: "Справится ли сервис с восемью участниками?",
        answer:
          "Ограничения по числу говорящих нет, но чем их больше и чем ближе тембры, тем выше риск спутать реплики. Фрагменты с наложением речи стоит проверять."
      },
      {
        question: "Как улучшить разделение по спикерам?",
        answer:
          "Помогает качественная запись: микрофон ближе к участникам, минимум фонового шума и по возможности отдельные микрофоны."
      },
      {
        question: "Подставляются ли имена участников?",
        answer:
          "Нет, они обозначаются как «Спикер 1», «Спикер 2» и так далее. Сопоставить их с именами нужно вручную при вычитке."
      },
      {
        question: "Что делать с двухчасовой записью?",
        answer: "Она обработается целиком — лимит составляет до 2 ГБ и до четырёх часов на файл."
      }
    ]
  },
  {
    path: "/research-transcription",
    cardTitle: "Для научной работы",
    metaTitle: "Расшифровка интервью для научной работы | Транскрибация исследований",
    metaDescription:
      "Расшифровка интервью и полевых записей для диплома, диссертации и научных исследований. Экономия десятков часов на перепечатке.",
    customTitle: "Расшифровка для научной работы",
    customSubtitle: "Интервью и полевые записи — в тексте, готовом к кодированию и цитированию.",
    seoDescription: (
      <>
        <p>
          В качественных исследованиях объём расшифровки измеряется десятками часов записей: серия интервью для
          диссертации легко даёт двадцать-тридцать часов материала. При ручной перепечатке это несколько недель работы
          до того, как начнётся собственно анализ.
        </p>
        <p>
          Автоматическая расшифровка переносит эти недели в анализ. Текст выгружается в DOCX и открывается в любом
          редакторе, а таймкоды позволяют вернуться к оригиналу записи, когда формулировка респондента принципиальна для
          аргумента.
        </p>
        <p>
          Академическая честность требует оговорки: расшифровку нужно вычитывать. Автоматический текст точен, но не
          безошибочен, и если вы цитируете респондента в работе, формулировку стоит сверить с записью.
        </p>
        <p>
          Полевые записи — самый непредсказуемый материал. Интервью в квартире информанта, на улице, в поле, в
          производственном цехе звучат совершенно по-разному, и единой оценки точности здесь быть не может. Разумный
          подход — прогнать одну запись из серии и по ней понять, чего ждать от остальных.
        </p>
        <p>
          Диалектная речь и региональная лексика распознаются хуже литературной нормы. Для диалектологии и устной
          истории это принципиально: автоматический текст в таких работах годится как основа для ручной доводки, но не
          как готовая транскрипция.
        </p>
        <p>
          Организационный совет: держите расшифровки и аудио в одной структуре папок с одинаковыми именами файлов. Через
          год, когда работа дойдёт до защиты, восстановить соответствие «текст — запись» по памяти будет невозможно, а
          сверять цитаты придётся.
        </p>
        <p>
          Про этику: информантов нужно предупреждать не только о записи, но и о том, что она будет обрабатываться
          автоматизированно. Это стандартное требование к исследованиям с участием людей, и его проще заложить в форму
          согласия сразу.
        </p>
      </>
    ),
    faq: [
      {
        question: "Можно ли цитировать расшифровку в научной работе?",
        answer:
          "Цитаты следует сверять с исходной записью. Автоматическая расшифровка точна, но не безошибочна, а в академическом тексте формулировка имеет значение."
      },
      {
        question: "В каком виде удобнее выгружать материал?",
        answer:
          "В DOCX — он открывается в любом редакторе и удобен для дальнейшего кодирования. Для чистого текста без разметки подойдёт TXT."
      },
      {
        question: "Подойдут ли полевые записи с диктофона?",
        answer:
          "Да, если речь разборчива. Сильный фоновый шум и большое расстояние до микрофона заметно снижают точность."
      },
      {
        question: "Есть ли ограничение на объём материала?",
        answer: "Ограничение только на один файл: до 2 ГБ и до четырёх часов. Общее количество записей не ограничено."
      }
    ]
  },
  {
    path: "/conference-transcription",
    cardTitle: "Для конференций",
    metaTitle: "Расшифровка конференции в текст | Транскрибация докладов и выступлений",
    metaDescription:
      "Расшифровка записей конференций, докладов и круглых столов в текст. Материал для публикации, сборника и субтитров к записи.",
    customTitle: "Расшифровка конференций и докладов",
    customSubtitle: "Выступления — в текст для публикации, сборника или субтитров.",
    seoDescription: (
      <>
        <p>
          После конференции остаётся видеозапись, которую мало кто пересматривает. Расшифровка превращает её в материал,
          который живёт дальше: тезисы для сборника, статьи в блог, текстовые версии докладов для тех, кто не смог
          присутствовать.
        </p>
        <p>
          Доклад — благодарный материал для распознавания: один говорящий, подготовленная речь, микрофон близко.
          Точность на таких записях стабильно высокая. Сложнее становится в секции вопросов из зала, где звук хуже, —
          здесь помогает разделение по спикерам, отделяющее вопросы от ответов.
        </p>
        <p>
          Выгрузка в SRT позволяет сразу приложить к записи субтитры, что заметно расширяет аудиторию видео и добавляет
          на страницу текст, который индексируется поисковиками.
        </p>
        <p>
          У конференций есть особенность, которой нет у других сценариев: докладов много, и они идут параллельными
          потоками. Расшифровка позволяет прочитать то, на что вы физически не могли попасть, — обычно это половина
          программы. Для участников это ценнее, чем сама запись, потому что читать быстрее, чем смотреть.
        </p>
        <p>
          Слайды в расшифровку не попадают, и в технических докладах это заметно: спикер говорит «вот здесь видно, что
          график растёт», а в тексте остаётся только эта фраза. Расшифровку таких выступлений стоит публиковать вместе с
          презентацией, иначе часть смысла теряется.
        </p>
        <p>
          Имена докладчиков, названия компаний и продуктов — главный источник ошибок. Модель распознаёт их по звучанию,
          и незнакомое название легко превращается в похожее слово. Перед публикацией их нужно вычитывать отдельно,
          особенно если материал идёт в сборник.
        </p>
        <p>
          Практический совет для организаторов: расшифровки прошлогодней конференции — хороший контент для сайта в
          межсезонье. Они приводят людей из поиска по темам докладов и заодно продают следующее мероприятие лучше любого
          анонса.
        </p>
      </>
    ),
    faq: [
      {
        question: "Можно ли расшифровать запись, опубликованную на видеохостинге?",
        answer:
          "Да, для открытых публикаций на YouTube, VK Video и Rutube достаточно вставить ссылку — скачивать файл не нужно."
      },
      {
        question: "Что с вопросами из зала?",
        answer:
          "Они распознаются хуже основного доклада, потому что говорящий дальше от микрофона. Разделение по спикерам при этом отделяет вопросы от ответов."
      },
      {
        question: "Как сделать субтитры к записи выступления?",
        answer: "Выгрузите расшифровку в SRT — этот файл подключается к видео как дорожка субтитров."
      },
      {
        question: "Подойдёт ли это для многодневной конференции?",
        answer: "Да, но записи нужно загружать по частям: ограничение составляет до четырёх часов и 2 ГБ на один файл."
      }
    ]
  },
  {
    path: "/training-transcription",
    cardTitle: "Для тренингов и курсов",
    metaTitle: "Расшифровка тренинга в текст | Транскрибация обучающих занятий",
    metaDescription:
      "Расшифровка записей тренингов, курсов и обучающих занятий в текст. Конспекты для участников и материалы для методички.",
    customTitle: "Расшифровка тренингов и занятий",
    customSubtitle: "Из записи занятия — конспект для участников и заготовка для методички.",
    seoDescription: (
      <>
        <p>
          Записи занятий обычно лежат мёртвым грузом: пересматривать двухчасовое видео ради одного объяснения никто не
          станет. В виде текста тот же материал становится рабочим — по нему ищут, из него собирают раздаточные
          материалы и конспекты.
        </p>
        <p>
          Для тренера это ещё и способ переиспользовать собственный материал: расшифровка нескольких занятий даёт основу
          для методички или курса в текстовом виде, где остаётся только структурировать и отредактировать устную речь в
          письменную.
        </p>
        <p>
          Участникам обычно достаточно AI-сводки — краткого изложения с ключевыми моментами занятия. Полный текст при
          этом остаётся под рукой для тех, кому нужны детали.
        </p>
        <p>
          Тренинг отличается от лекции интерактивностью: половина ценного происходит в упражнениях и разборе кейсов, а
          не в монологе ведущего. Именно эти части обычно и не записывают — камера направлена на тренера, а группа
          работает где-то за кадром. Если разборы важны, микрофон нужен и у группы.
        </p>
        <p>
          Из расшифровок серии занятий получается то, что дороже отдельного конспекта, — база вопросов участников. Из
          неё видно, что именно люди не понимают из раза в раз, и программу можно править по фактам, а не по ощущениям.
        </p>
        <p>
          Записи экрана с обучающих занятий обрабатываются так же, как обычное видео: сервис берёт звук, картинку
          игнорирует. Но если тренер много показывает молча, в тексте останутся паузы — такие фрагменты лучше дополнить
          скриншотами вручную.
        </p>
        <p>
          Для платных курсов расшифровка решает ещё и практическую задачу доступности: текстовая версия нужна тем, кто
          учится с плохим интернетом, в дороге или просто быстрее читает, чем смотрит. Это заметно снижает число
          вопросов «а можно повторить».
        </p>
      </>
    ),
    faq: [
      {
        question: "Как быстро сделать конспект занятия?",
        answer:
          "Запустите AI-сводку после расшифровки: она соберёт краткое изложение и ключевые моменты. Полный текст останется рядом."
      },
      {
        question: "Распознаются ли реплики участников, а не только тренера?",
        answer:
          "Да, если они попали в запись разборчиво. Разделение по спикерам разведёт объяснения тренера и вопросы группы."
      },
      {
        question: "Можно ли из расшифровки сделать текстовый курс?",
        answer:
          "Да, это частый сценарий. Устную речь придётся отредактировать в письменную, но структура и содержание уже будут готовы."
      },
      {
        question: "Что делать с записью экрана?",
        answer:
          "Загружайте её как обычное видео — сервис извлечёт аудиодорожку, а изображение для расшифровки не используется."
      }
    ]
  }
];
